<script setup lang="ts">
	import { ref, watch } from "vue";

	const props = withDefaults(
		defineProps<{
			modelValue: boolean;
			loading?: boolean;
		}>(),
		{
			loading: false
		}
	);

	const emit = defineEmits<{
		"update:modelValue": [value: boolean];
		cancel: [];
		review: [];
		continue: [options: { markReviewed: boolean }];
	}>();

	const doNotShowAgain = ref(false);

	watch(
		() => props.modelValue,
		(open) => {
			if (open) {
				doNotShowAgain.value = false;
			}
		}
	);

	function onCancel() {
		emit("update:modelValue", false);
		emit("cancel");
	}

	function onReview() {
		emit("review");
	}

	function onContinue() {
		if (props.loading) return;

		emit("continue", { markReviewed: doNotShowAgain.value });
	}
</script>

<template>
	<v-dialog :model-value="modelValue" max-width="560" persistent @update:model-value="emit('update:modelValue', $event)">
		<v-card class="unreviewed-guides-dialog px-8 py-7" rounded="lg" elevation="8">
			<h5 class="unreviewed-title">Unreviewed Spark Guides</h5>
			<p class="unreviewed-copy">
				This Spark has guides you have not reviewed yet. Reviewing them helps keep your reporting accurate.
			</p>

			<v-checkbox
				v-model="doNotShowAgain"
				class="dismiss-checkbox"
				density="comfortable"
				hide-details
				:disabled="loading"
				label="Do not show this alert again." />

			<div class="d-flex justify-end ga-3 actions">
				<v-btn class="dialog-action" variant="outlined" rounded="xl" :disabled="loading" @click="onCancel">Cancel</v-btn>
				<PrimaryBtn class="dialog-action review-action" :disabled="loading" @click="onReview"> Review Spark Guides </PrimaryBtn>
				<v-btn
					class="dialog-action continue-action"
					variant="outlined"
					rounded="xl"
					:loading="loading"
					:disabled="loading"
					@click="onContinue">
					Continue
				</v-btn>
			</div>
		</v-card>
	</v-dialog>
</template>

<style scoped lang="scss">
	.unreviewed-guides-dialog {
		border-radius: 8px !important;
	}

	.unreviewed-title {
		margin: 0 0 16px;
		color: rgb(var(--v-theme-on-surface));
		font-size: 1.375rem;
		font-weight: 700;
		line-height: 1.25;
		letter-spacing: 0;
	}

	.unreviewed-copy {
		margin: 0;
		color: rgba(var(--v-theme-on-surface), 0.68);
		font-size: 1rem;
		line-height: 1.5;
		letter-spacing: 0;
	}

	.dismiss-checkbox {
		display: inline-flex;
		margin-top: 28px;
		margin-bottom: 28px;
	}

	.dismiss-checkbox :deep(.v-label) {
		color: rgba(var(--v-theme-on-surface), 0.84);
		font-size: 0.95rem;
	}

	.actions {
		align-items: stretch;
	}

	.dialog-action {
		height: 44px;
		min-width: 116px;
		padding-inline: 20px;
		font-size: 0.9rem;
		font-weight: 600;
		letter-spacing: 0;
		text-transform: none;
	}

	.dialog-action :deep(.v-btn__content) {
		font-size: 0.9rem;
		font-weight: 600;
		line-height: 1.2;
		letter-spacing: 0;
	}

	.review-action {
		min-width: 212px;
	}

	.continue-action {
		min-width: 128px;
	}

	@media #{map.get($display-breakpoints, 'xs')} {
		.unreviewed-guides-dialog {
			padding-left: 24px !important;
			padding-right: 24px !important;
		}

		.unreviewed-title {
			font-size: 1.25rem;
		}

		.unreviewed-copy {
			font-size: 1rem;
		}

		.actions {
			flex-direction: column;
		}

		.dialog-action {
			width: 100%;
		}
	}
</style>
